import React, { useState, useRef, useEffect } from "react";
import { useCart } from "../context/CartContext";
import SpiceSelector from "./SpiceSelector";
import { SpiceLevel } from "../types/menu";
import { CURRENCY } from '../config/currency';

interface SimpleItemSelectorProps {
  item: {
    id: number;
    name: string;
    price: number;
    allowsSpiceCustomization?: boolean;
    spiceLevel?: SpiceLevel;
  };
  onClose: () => void;
}

/**
 * Simple selector for items without portions but with spice customization
 */
const SimpleItemSelector: React.FC<SimpleItemSelectorProps> = ({ item, onClose }) => {
  const { addToCart } = useCart();
  const [selectedSpiceLevel, setSelectedSpiceLevel] = useState<SpiceLevel | null>(
    item.spiceLevel || null
  );
  const [quantity, setQuantity] = useState(1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [positionClass, setPositionClass] = useState("md:top-full md:mt-2");

  // Viewport-aware positioning
  useEffect(() => {
    const checkPosition = () => {
      if (dropdownRef.current) {
        const dropdown = dropdownRef.current;
        const rect = dropdown.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Get parent container position
        const parent = dropdown.parentElement;
        if (parent) {
          const parentRect = parent.getBoundingClientRect();
          const spaceBelow = viewportHeight - parentRect.bottom;
          const spaceAbove = parentRect.top;
          const dropdownHeight = rect.height || 300; // Estimated height if not yet rendered

          // If not enough space below, show above
          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setPositionClass("md:bottom-full md:mb-2");
          } else {
            setPositionClass("md:top-full md:mt-2");
          }
        }
      }
    };

    // Check position on mount and when quantity/spice changes (affects height)
    checkPosition();

    // Recheck on window resize and scroll
    window.addEventListener('resize', checkPosition);
    window.addEventListener('scroll', checkPosition, true);

    return () => {
      window.removeEventListener('resize', checkPosition);
      window.removeEventListener('scroll', checkPosition, true);
    };
  }, [quantity, selectedSpiceLevel]);

  const handleAddToCart = () => {
    addToCart({
      id: item.id,
      name: item.name,
      price: item.price,
      requestedSpiceLevel: selectedSpiceLevel || undefined
    }, quantity);
    onClose();
  };

  return (
    <div
      ref={dropdownRef}
      className={`fixed md:absolute top-1/2 ${positionClass} left-1/2 md:left-0 right-auto md:right-0 -translate-x-1/2 -translate-y-1/2 md:translate-x-0 md:translate-y-0 z-50 bg-white shadow-2xl md:shadow-lg rounded-xl border border-gray-200 p-4 w-[90vw] md:w-auto md:min-w-[320px] max-w-md`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-gray-800">{item.name}</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 text-lg"
        >
          ×
        </button>
      </div>

      {/* Quantity Selector */}
      <div className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2 mb-3">
        <span className="font-medium text-gray-700">Quantity</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-7 h-7 flex items-center justify-center bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition"
          >
            −
          </button>
          <span className="min-w-[16px] text-center text-gray-800 font-bold text-lg">
            {quantity}
          </span>
          <button
            type="button"
            onClick={() => setQuantity(quantity + 1)}
            className="w-7 h-7 flex items-center justify-center bg-gray-800 text-white rounded-full hover:bg-gray-700 transition"
          >
            +
          </button>
        </div>
      </div>

      {/* Spice Level Selector */}
      {item.allowsSpiceCustomization && (
        <div className="mb-3">
          <SpiceSelector
            selectedLevel={selectedSpiceLevel}
            onChange={setSelectedSpiceLevel}
            size="small"
          />
        </div>
      )}

      <div className="flex justify-between items-center border-t pt-3 mt-2">
        <span className="font-semibold text-gray-800">Total: {CURRENCY.symbol}{(item.price * quantity).toFixed(2)}</span>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleAddToCart}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-700 transition"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleItemSelector;
